import React, { useState } from "react";
import { Phone, Mail, MessageCircle, User, Plus, Tag, FileText, Check, Clock, X } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

const TIMELINE_COLOR = "#4338ca";

const TYPE_META = {
  call:            { Icon: Phone,         color: "#1d4ed8" },
  email:           { Icon: Mail,          color: "#7c3aed" },
  whatsapp:        { Icon: MessageCircle, color: "#16a34a" },
  stage_change:    { Icon: User,          color: "#1d4ed8" },
  category_change: { Icon: Tag,           color: "#b45309" },
  note:            { Icon: FileText,      color: "#7c3aed" },
  reminder_set:    { Icon: Plus,          color: "#1d4ed8" },
  reminder_done:   { Icon: Check,         color: "#15803d" },
  reminder_snooze: { Icon: Clock,         color: "#b45309" },
  created:         { Icon: Plus,          color: "#475569" },
  name_change:     { Icon: User,          color: "#475569" },
};

function formatLongIST(iso) {
  if (!iso) return "";
  try {
    const s = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(iso));
    return s.replace(/\bam\b/g, "AM").replace(/\bpm\b/g, "PM");
  } catch {
    return iso;
  }
}

function ActionBtn({ icon: Icon, label, color, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[6px] border border-[#E8EAEF] bg-white hover:bg-[#F0F1F7] hover:border-[#6C47FF] text-xs font-[600] text-[#1A1A2E] transition-colors"
    >
      <Icon className="h-3.5 w-3.5" style={{ color }} />
      {label}
    </button>
  );
}

export default function ActivityTimeline({ lead, onUpdated }) {
  const [showCount, setShowCount] = useState(20);
  const [logModal, setLogModal] = useState(null);
  const [logBody, setLogBody] = useState({ outcome: "Answered", direction: "Sent", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const activities = (lead?.activities || []).slice().reverse();
  const visible = activities.slice(0, showCount);

  const openLog = (type) => {
    setLogBody({ outcome: "Answered", direction: "Sent", note: "" });
    setLogModal(type);
  };

  const submitLog = async () => {
    if (logModal === "note" && !logBody.note.trim()) return toast.error("Note is required");
    const payload = { type: logModal, note: logBody.note };
    if (logModal === "call") payload.outcome = logBody.outcome;
    if (logModal === "whatsapp" || logModal === "email") payload.direction = logBody.direction;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/log`, payload);
      onUpdated && onUpdated(data);
      setLogModal(null);
      toast.success("Activity logged");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const TITLES = { call: "Log a Call", whatsapp: "Log WhatsApp", email: "Log Email", note: "Add a Note" };

  return (
    <div data-testid="activity-timeline">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-xs font-[600] uppercase tracking-wider text-[#6B7280]">Activity Timeline</h3>
        <div className="flex gap-1.5 flex-wrap">
          <ActionBtn icon={Phone} label="Log Call" color="#1d4ed8" onClick={() => openLog("call")} testid="log-call-btn" />
          <ActionBtn icon={MessageCircle} label="Log WhatsApp" color="#16a34a" onClick={() => openLog("whatsapp")} testid="log-whatsapp-btn" />
          <ActionBtn icon={Mail} label="Log Email" color="#7c3aed" onClick={() => openLog("email")} testid="log-email-btn" />
          <ActionBtn icon={FileText} label="Add Note" color="#475569" onClick={() => openLog("note")} testid="log-note-btn" />
        </div>
      </div>

      <div className="relative max-h-[360px] overflow-y-auto pr-2" data-testid="timeline-list">
        {visible.length > 0 && (
          <div
            className="absolute top-3 bottom-3 w-[2px]"
            style={{ left: "15px", backgroundColor: TIMELINE_COLOR, opacity: 0.25 }}
          />
        )}
        <div className="space-y-2">
          {visible.map((a, i) => {
            const meta = TYPE_META[a.type] || TYPE_META.note;
            const Icon = meta.Icon;
            const showBy = a.by && a.by !== "Import" && a.by !== "System" && !a.message.includes(a.by);
            return (
              <div
                key={i}
                className="relative flex gap-3 py-2 pr-2 rounded-[6px] transition-colors hover:bg-[rgba(124,58,237,0.04)]"
                data-testid={`timeline-item-${i}`}
              >
                <div
                  className="flex-shrink-0 h-8 w-8 rounded-full bg-white border-2 flex items-center justify-center z-10"
                  style={{ borderColor: meta.color }}
                >
                  <Icon className="h-4 w-4" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-[#6B7280] font-[460]">{formatLongIST(a.at)}</div>
                  <div className="text-sm text-[#1A1A2E] leading-snug">
                    {a.message}
                    {showBy && <span className="text-[#6B7280]"> · by {a.by}</span>}
                  </div>
                </div>
              </div>
            );
          })}
          {visible.length === 0 && <div className="text-xs text-[#6B7280] py-6 text-center">No activity yet.</div>}
        </div>
      </div>

      {showCount < activities.length && (
        <button
          onClick={() => setShowCount((c) => c + 20)}
          data-testid="timeline-load-more"
          className="mt-3 text-xs px-3 py-1.5 rounded-[6px] border border-[#6C47FF] font-[600]"
        >
          Load more ({activities.length - showCount} remaining)
        </button>
      )}

      <Dialog open={!!logModal} onOpenChange={(v) => !v && setLogModal(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="log-modal">
          <DialogHeader>
            <DialogTitle>{logModal ? TITLES[logModal] : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {logModal === "call" && (
              <div>
                <label className="text-xs text-[#6B7280]">Outcome</label>
                <select
                  value={logBody.outcome}
                  onChange={(e) => setLogBody({ ...logBody, outcome: e.target.value })}
                  data-testid="log-call-outcome"
                  className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
                >
                  {["Answered", "Not Picking", "Busy", "Call Back Later"].map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            )}
            {(logModal === "whatsapp" || logModal === "email") && (
              <div>
                <label className="text-xs text-[#6B7280]">Direction</label>
                <select
                  value={logBody.direction}
                  onChange={(e) => setLogBody({ ...logBody, direction: e.target.value })}
                  data-testid={`log-${logModal}-direction`}
                  className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
                >
                  <option value="Sent">Sent</option>
                  <option value="Received">Received</option>
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-[#6B7280]">{logModal === "note" ? "Note *" : "Note (optional)"}</label>
              <textarea
                value={logBody.note}
                onChange={(e) => setLogBody({ ...logBody, note: e.target.value })}
                rows={3}
                data-testid="log-note-input"
                className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 text-sm bg-white"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setLogModal(null)} className="inline-flex items-center gap-1 px-4 py-2 rounded-[8px] border border-[#6C47FF] text-sm font-[600] min-h-[44px]">
                <X className="h-3 w-3" /> Cancel
              </button>
              <button
                onClick={submitLog}
                disabled={submitting}
                data-testid="log-submit"
                className="px-4 py-2 rounded-[8px] btn-mx text-sm font-[600] min-h-[44px] disabled:opacity-60"
              >
                {submitting ? "Logging…" : "Log"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
